import { useLayoutEffect, useSyncExternalStore } from 'react';
import { z } from 'zod';

const STORAGE_KEY = 'phoebe-theme';

const themeSchema = z.enum(['light', 'dark', 'system']);
export type Theme = z.infer<typeof themeSchema>;

// Default is light: the site never goes dark on its own — dark (or following
// the OS via 'system') is an explicit per-user opt-in from the toggle.
function parseTheme(value: unknown): Theme {
    const result = themeSchema.safeParse(value);
    return result.success ? result.data : 'light';
}

let currentTheme: Theme | null = null;
const listeners = new Set<() => void>();
let isWatchingEnvironment = false;

function readStoredTheme(): Theme {
    let storedTheme: string | null = null;
    try {
        storedTheme = localStorage.getItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing, etc.)
    }
    return parseTheme(storedTheme);
}

// matchMedia is absent in the jsdom test environment; treat that as light.
function systemPrefersDark(): boolean {
    return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );
}

// The `dark` class drives the .dark token palette in global.css. index.html
// applies it before first paint with the same storage key + system fallback,
// so this only needs to keep the class in sync after hydration.
function applyThemeClass(theme: Theme) {
    const isDark =
        theme === 'dark' || (theme === 'system' && systemPrefersDark());
    document.documentElement.classList.toggle('dark', isDark);
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

function ensureCurrentTheme(): Theme {
    if (currentTheme === null) {
        currentTheme = readStoredTheme();
        applyThemeClass(currentTheme);
    }
    if (!isWatchingEnvironment) {
        isWatchingEnvironment = true;
        // Follow OS appearance changes while in 'system'.
        if (typeof window.matchMedia === 'function') {
            window
                .matchMedia('(prefers-color-scheme: dark)')
                .addEventListener('change', () => {
                    if (currentTheme === 'system') {
                        applyThemeClass(currentTheme);
                        emitChange();
                    }
                });
        }
        // Follow theme changes made in another tab.
        window.addEventListener('storage', (event) => {
            if (event.key === STORAGE_KEY && event.newValue !== null) {
                const newTheme = parseTheme(event.newValue);
                if (newTheme !== currentTheme) {
                    currentTheme = newTheme;
                    applyThemeClass(newTheme);
                    emitChange();
                }
            }
        });
    }
    return currentTheme;
}

export const themeStore = {
    getTheme(): Theme {
        return ensureCurrentTheme();
    },
    setTheme(theme: Theme) {
        const previousTheme = ensureCurrentTheme();
        if (theme === previousTheme) {
            return;
        }
        currentTheme = theme;
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // localStorage unavailable (private browsing, quota, etc.)
        }
        applyThemeClass(theme);
        emitChange();
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function useTheme(): Theme {
    return useSyncExternalStore(themeStore.subscribe, themeStore.getTheme);
}

// Dark mode is a platform-admin-only rollout. Mount this once at the app root:
// the theme toggle lives in the sidebar footer, which is not mounted at all on
// mobile until the drawer is opened, so it cannot be what enforces the
// restriction. Writing light through rather than just stripping the class also
// clears a preference persisted while the toggle was ungated, so index.html's
// pre-paint script stops applying dark on every later load. `isAdmin` is
// undefined until the user loads.
//
// This observes the theme rather than only `isAdmin` so that a later dark
// write from another tab, which the store applies through its own storage
// listener, is forced back to light instead of persisting for the session.
//
// The correction runs in the layout phase because index.html has already
// applied the stored dark class: a passive effect would let the browser paint
// one dark frame of the authenticated UI first.
export function useDarkThemeAccess(isAdmin: boolean | undefined) {
    const theme = useTheme();
    useLayoutEffect(() => {
        if (isAdmin === undefined || isAdmin || theme === 'light') {
            return;
        }
        themeStore.setTheme('light');
    }, [isAdmin, theme]);
}
